import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Layers, ArrowUpRight, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const AREAS = {
  gout: { label: "GOÛT Studio", color: "#6D28D9" },
  tagma: { label: "Tagma Studio", color: "#DB2777" },
  aura: { label: "AURA Commerce", color: "#B45309" },
  vetta: { label: "VETTA Factory", color: "#0A1428" },
  retail_po: { label: "Órdenes de Compra (Retail)", color: "#0891B2" },
};
const WORKSPACE = new Set(["vetta", "aura"]);

export default function ConnectedAreas({ module, entityId }) {
  const [connected, setConnected] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/projects/connections/${module}/${entityId}`)
      .then((r) => setConnected(r.data.connected || [])).catch(() => {});
  }, [module, entityId]);
  useEffect(() => { load(); }, [load]);

  const others = Object.keys(AREAS).filter((m) => m !== module);
  const byModule = (m) => connected.filter((c) => c.module === m);

  const activate = async (target) => {
    setBusy(target);
    try {
      await api.post(`/projects/connections/activate`, { module, entity_id: entityId, target_module: target });
      toast.success(`Creado y conectado en ${AREAS[target].label}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(""); }
  };
  const disconnect = async (c) => {
    try { await api.delete(`/projects/connections/${module}/${entityId}/${c.module}/${c.id}`); toast.success("Desconectado"); load(); }
    catch (e) { toast.error("Error"); }
  };

  return (
    <div className="bg-white border border-navy/10 rounded-lg p-5" data-testid="connected-areas">
      <div className="flex items-center gap-2 mb-1 text-navy/70">
        <Layers className="w-4 h-4" /><span className="font-medium text-sm">Trabajar en otras áreas</span>
      </div>
      <p className="text-xs text-navy/45 mb-4">Conecta este proyecto con otras áreas para trabajarlo con sus herramientas reales. El mismo proyecto, distintas herramientas.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {others.map((m) => {
          const items = byModule(m);
          return (
            <div key={m} data-testid={`connect-area-${m}`} className="border border-navy/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: AREAS[m].color }} />
                <span className="text-sm font-medium">{AREAS[m].label}</span>
              </div>
              {items.length > 0 && (
                <div className="space-y-1.5 mb-3">
                  {items.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 group">
                      <Link to={c.url} data-testid={`connect-open-${c.id}`}
                        className="flex-1 min-w-0 flex items-center gap-1.5 text-sm text-navy hover:text-violet transition-colors">
                        <ArrowUpRight className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{c.name}</span>
                      </Link>
                      <button data-testid={`connect-remove-${c.id}`} onClick={() => disconnect(c)}
                        className="text-[11px] text-navy/30 hover:text-destructive transition-colors opacity-0 group-hover:opacity-100">quitar</button>
                    </div>
                  ))}
                </div>
              )}
              <button data-testid={`connect-activate-${m}`} onClick={() => activate(m)} disabled={busy === m}
                className={`inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-violet hover:text-violet transition-colors disabled:opacity-50 ${WORKSPACE.has(m) && items.length > 0 ? "hidden" : ""}`}>
                {busy === m ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                {WORKSPACE.has(m)
                  ? `Activar ${AREAS[m].label}`
                  : (items.length > 0 ? `Nuevo en ${AREAS[m].label}` : `Activar ${AREAS[m].label}`)}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
